"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const node_1 = require("./node");
const cluster_1 = require("./cluster");
const vector_1 = require("../core/vector");
class Pathfinding {
    constructor(world, algorithm, clusterSize) {
        this.world = world;
        this.algorithm = algorithm;
        this.clusterSize = clusterSize;
        this.nodes = [];
        this.tempNodes = new WeakMap();
        if (clusterSize < 3)
            throw new Error('Cluster size has to be an integer bigger than 2');
        this.size = new vector_1.default(world.size.x / clusterSize, world.size.y / clusterSize);
        this.clusters = [];
        const areaSize = new vector_1.default(clusterSize, clusterSize);
        for (let j = 0; j < this.size.y; j++) {
            this.clusters[j] = [];
            for (let i = 0; i < this.size.x; i++) {
                const range = world.getRange(areaSize.multiply(i, j), areaSize);
                this.clusters[j][i] = new cluster_1.Cluster(world, algorithm, new vector_1.default(i, j), range);
            }
        }
        this.processConnections();
    }
    resolve(start, end) {
        if (start.isObstacle || end.isObstacle)
            return null;
        const startCluster = this.getClusterFor(start);
        if (startCluster === this.getClusterFor(end)) {
            return {
                levels: [],
                tiles: startCluster.resolve(start, end),
            };
        }
        const startNode = this.getTempNodeFor(start);
        const endNode = this.getTempNodeFor(end);
        const result = this.resolveInternal(startNode, endNode);
        startNode.disconnect();
        endNode.disconnect();
        return result;
    }
    resolveInternal(startNode, endNode) {
        const path = this.algorithm.getPath(startNode, endNode);
        if (!path)
            return null;
        const tiles = [];
        let prev = startNode;
        for (const step of path) {
            const between = this.fixBridges(last(tiles), this.getTilesBetween(prev, step));
            tiles.push(between);
            prev = step;
        }
        return {
            levels: [path],
            tiles: [].concat(...tiles),
        };
    }
    fixBridges(prev, current) {
        if (!prev)
            return current;
        const lastTile = last(prev);
        const firstTile = current[0];
        if (lastTile.isNeighbor(firstTile))
            return current;
        return [
            ...this.algorithm.getPath(lastTile, current[0]),
            ...current.slice(1),
        ];
    }
    getTilesBetween(start, end) {
        const { childA, childB } = start.getRelation(end);
        const isSorted = start.hasChild(childA);
        const startTile = isSorted ? childA : childB;
        const endTile = isSorted ? childB : childA;
        const cluster = this.getClusterFor(startTile);
        if (this.getClusterFor(endTile) !== cluster)
            throw new Error('Tiles are from different clusters');
        return cluster.resolve(startTile, endTile);
    }
    processConnections() {
        this.forEach(cluster => {
            for (const entrance of cluster.processEntrances()) {
                const node = this.getNodeFor(entrance);
                for (const [connection, path] of cluster.getConnections(entrance)) {
                    const connectionNode = this.getNodeFor(connection);
                    const relation = {
                        cost: this.algorithm.getCost(entrance, path),
                        childA: entrance,
                        childB: connection,
                    };
                    node.setNeighbor(connectionNode, relation);
                    connectionNode.setNeighbor(node, relation);
                }
            }
        });
        /*
        this.nodes.map(node => {
          let message = '';
    
          for (let [ neighbor, relation ] of node.getNeighbors()) {
            message += `${node.id} - ${(neighbor as Node).id} = ${relation.cost}\n`;
          }
    
          console.log(message);
        });
        */
    }
    findNode(child) {
        return this.nodes.find(node => node.hasChild(child));
    }
    getNodeFor(child) {
        let node = this.findNode(child);
        if (!node) {
            node = new node_1.Node();
            node.addChild(child);
            this.nodes.push(node);
        }
        return node;
    }
    getTempNodeFor(child) {
        if (this.tempNodes.has(child))
            return this.tempNodes.get(child).reconnect();
        const cluster = this.getClusterFor(child);
        const node = new node_1.TemporalNode();
        node.addChild(child);
        for (const [connection, path] of cluster.getConnections(child)) {
            node.setNeighbor(this.getNodeFor(connection), {
                cost: this.algorithm.getCost(child, path),
                childA: child,
                childB: connection,
            });
        }
        const overlappingNode = this.findNode(child);
        if (overlappingNode) {
            node.setNeighbor(overlappingNode, {
                cost: 0,
                childA: child,
                childB: child,
            });
        }
        this.tempNodes.set(child, node);
        return node.reconnect();
    }
    getClusterFor(child) {
        const index = child.location
            .divide(this.clusterSize)
            .apply(Math.floor);
        return this.clusters[index.y][index.x];
    }
    forEach(iterator) {
        for (let row of this.clusters)
            for (let cluster of row)
                iterator(cluster, this);
    }
}
exports.Pathfinding = Pathfinding;
function last(list) {
    return list[list.length - 1];
}



//////////////////
// WEBPACK FOOTER
// ./src/pathfinding/pathfinding.ts
// module id = 6
// module chunks = 0